import { AlertTriangle, Loader2 } from "lucide-react";

import { showConfirm } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { TotemPendingWithRelations } from "~/services/totemService";
import { useProfileStore } from "~/store/profileStore";

import { useResolvePending } from "../hooks/usePendingAssignments";
import { useAssignTotemsToClient } from "../hooks/useTotems";
import { getBucket } from "../utils/buckets";

interface ResolvePendingDialogProps {
  pending: TotemPendingWithRelations | null;
  // Totems currently in the "Disponible" bucket (available + filled).
  availableCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DISPONIBLE_STATUSES = getBucket("disponible").statuses;

export function ResolvePendingDialog({
  pending,
  availableCount,
  open,
  onOpenChange,
}: ResolvePendingDialogProps) {
  const { id: userId } = useProfileStore();
  const assignToClient = useAssignTotemsToClient();
  const resolvePending = useResolvePending();

  const isPending = assignToClient.isPending || resolvePending.isPending;
  const toAssign = pending
    ? Math.min(pending.pending_count, availableCount)
    : 0;

  const handleAssign = async () => {
    if (!pending || !pending.client_id || !userId || toAssign <= 0) return;

    const moved = await assignToClient.mutateAsync({
      fromStatuses: DISPONIBLE_STATUSES,
      quantity: toAssign,
      clientId: pending.client_id,
      performedBy: userId,
      notes: `Asignado al cliente (resolución de pendiente Factura ${
        pending.bsale_number ?? ""
      })`.trim(),
    });

    await resolvePending.mutateAsync({
      id: pending.id,
      performedBy: userId,
      assignedNow: moved,
    });

    onOpenChange(false);
  };

  const handleManualResolve = async () => {
    if (!pending || !userId) return;

    const result = await showConfirm({
      title: "Marcar como resuelto",
      text: "Se cerrará la alarma sin mover tótems. Úsalo solo si ya moviste los tótems al cliente por otra vía.",
      confirmButtonText: "Sí, marcar resuelto",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    await resolvePending.mutateAsync({
      id: pending.id,
      performedBy: userId,
      notes: "Marcado como resuelto manualmente",
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Resolver pendiente</DialogTitle>
          <DialogDescription>
            {pending?.client?.name ?? "Cliente"} —{" "}
            {pending?.product?.short_name ?? "Producto"}
          </DialogDescription>
        </DialogHeader>

        {pending && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-2 rounded-lg border border-gray-200 bg-gray-100/80 p-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Requeridos</p>
                <p className="text-lg font-semibold text-gray-800">
                  {pending.required_count}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Asignados</p>
                <p className="text-lg font-semibold text-gray-800">
                  {pending.assigned_count}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pendientes</p>
                <p className="text-lg font-semibold text-red-700">
                  {pending.pending_count}
                </p>
              </div>
            </div>

            {availableCount > 0 ? (
              <p className="text-sm text-gray-700">
                Hay <span className="font-semibold">{availableCount}</span>{" "}
                tótem(s) en Disponible. Se asignarán{" "}
                <span className="font-semibold">{toAssign}</span> al cliente y
                se {toAssign >= pending.pending_count ? "cerrará" : "reducirá"}{" "}
                la alarma.
              </p>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-100/80 p-3 text-sm text-gray-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  No hay tótems en Disponible para mover. Registra los tótems
                  físicos primero (Agregar Totem o retorno a Disponible), o marca
                  esta alarma como resuelta si ya los moviste al cliente.
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="h-9"
            onClick={handleManualResolve}
            disabled={isPending}
          >
            Marcar como resuelto
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="h-9"
              onClick={handleAssign}
              disabled={isPending || toAssign <= 0}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Asignar {toAssign} y resolver
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
